import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import TemuOpsPager from '@/temu-platform/components/TemuOpsPager';
import { collectMarketingEnrollSiteOptions } from '@/temu-platform/utils/temuMarketingEnroll';

const STEP_SESSIONS = 'sessions';
const STEP_SITES = 'sites';
const PAGE_SIZE_OPTIONS = [50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;

/**
 * 报名向导：选择场次 → 选择站点 → 确认提交
 * 单条 / 批量共用。needsSessions=false 时跳过场次步。
 */
export default function MarketingEnrollWizardModal({
  open = false,
  products = [],
  needsSessions = true,
  sessions = [],
  sessionsLoading = false,
  sessionsError = '',
  submitting = false,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation('workbench');
  const [step, setStep] = useState(needsSessions ? STEP_SESSIONS : STEP_SITES);
  const [selectedSessionIds, setSelectedSessionIds] = useState(() => new Set());
  const [selectedSiteIds, setSelectedSiteIds] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const productCount = Array.isArray(products) ? products.length : 0;
  const sessionList = useMemo(
    () => (Array.isArray(sessions) ? sessions : []),
    [sessions],
  );

  useEffect(() => {
    if (!open) return undefined;
    setStep(needsSessions ? STEP_SESSIONS : STEP_SITES);
    setSelectedSessionIds(new Set());
    setSelectedSiteIds(new Set());
    setPage(1);
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, needsSessions, products, onClose, submitting]);

  useEffect(() => {
    setPage(1);
  }, [step, sessionList.length]);

  const selectedSessions = useMemo(
    () => sessionList.filter(
      (row) => row?.sessionId != null && selectedSessionIds.has(String(row.sessionId)),
    ),
    [sessionList, selectedSessionIds],
  );

  const siteOptions = useMemo(() => {
    if (needsSessions) {
      return collectMarketingEnrollSiteOptions(products, selectedSessions);
    }
    return collectMarketingEnrollSiteOptions(products, sessionList);
  }, [needsSessions, products, selectedSessions, sessionList]);

  useEffect(() => {
    if (!open || step !== STEP_SITES) return;
    setSelectedSiteIds(new Set(siteOptions.map((s) => String(s.siteId))));
    setPage(1);
  }, [open, step, siteOptions]);

  const listForStep = step === STEP_SESSIONS ? sessionList : siteOptions;
  const total = listForStep.length;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return listForStep.slice(start, start + pageSize);
  }, [listForStep, safePage, pageSize]);

  if (!open) return null;

  const toggleSession = (sessionId) => {
    if (sessionId == null) return;
    const key = String(sessionId);
    setSelectedSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSite = (siteId) => {
    const key = String(siteId);
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const pageSessionIds = step === STEP_SESSIONS
    ? pageRows.filter((row) => row?.sessionId != null).map((row) => String(row.sessionId))
    : [];
  const pageSiteIds = step === STEP_SITES
    ? pageRows.map((row) => String(row.siteId))
    : [];

  const allPageSessionsSelected = pageSessionIds.length > 0
    && pageSessionIds.every((id) => selectedSessionIds.has(id));
  const allPageSitesSelected = pageSiteIds.length > 0
    && pageSiteIds.every((id) => selectedSiteIds.has(id));

  const toggleAllSessions = () => {
    setSelectedSessionIds((prev) => {
      const next = new Set(prev);
      if (allPageSessionsSelected) {
        pageSessionIds.forEach((id) => next.delete(id));
      } else {
        pageSessionIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleAllSites = () => {
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      if (allPageSitesSelected) {
        pageSiteIds.forEach((id) => next.delete(id));
      } else {
        pageSiteIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const goNextFromSessions = () => {
    if (needsSessions && selectedSessionIds.size === 0) return;
    setStep(STEP_SITES);
  };

  const handleSubmit = () => {
    if (selectedSiteIds.size === 0) return;
    let sessionIds = Array.from(selectedSessionIds).map(Number).filter((n) => Number.isFinite(n));
    if (needsSessions && selectedSessions.length) {
      sessionIds = selectedSessions
        .filter((row) => {
          if (row?.siteId == null) return true;
          return selectedSiteIds.has(String(row.siteId));
        })
        .map((row) => Number(row.sessionId))
        .filter((n) => Number.isFinite(n));
    }
    onSubmit?.({
      products,
      sessionIds,
      siteIds: Array.from(selectedSiteIds).map(Number).filter((n) => Number.isFinite(n)),
    });
  };

  const steps = needsSessions
    ? [
      { key: STEP_SESSIONS, label: '选择场次' },
      { key: STEP_SITES, label: '选择站点' },
      { key: 'submit', label: t('marketing.enSubmit') },
    ]
    : [
      { key: STEP_SITES, label: '选择站点' },
      { key: 'submit', label: t('marketing.enSubmit') },
    ];

  const activeStepIndex = steps.findIndex((s) => s.key === step);

  return createPortal(
    <div
      className="tpo-batch-modal tpo-enroll-wizard-modal"
      role="presentation"
      onClick={() => !submitting && onClose?.()}
    >
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        tabIndex={-1}
        onClick={() => !submitting && onClose?.()}
      />
      <div
        className="tpo-batch-modal__panel tpo-enroll-wizard-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-label={fixedUi("报名向导")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tpo-enroll-wizard-modal__head">
          <div className="tpo-enroll-wizard-modal__steps" aria-label={fixedUi("报名步骤")}>
            {steps.map((item, index) => {
              const isCurrent = index === activeStepIndex;
              const isDone = index < activeStepIndex;
              return (
                <React.Fragment key={item.key}>
                  {index > 0 ? (
                    <span className="tpo-enroll-wizard-modal__step-sep" aria-hidden="true">→</span>
                  ) : null}
                  <span
                    className={[
                      'tpo-enroll-wizard-modal__step',
                      isCurrent ? 'is-active' : '',
                      isDone ? 'is-done' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <span className="tpo-enroll-wizard-modal__step-index">{index + 1}</span>
                    <span className="tpo-enroll-wizard-modal__step-label">{item.label}</span>
                  </span>
                </React.Fragment>
              );
            })}
          </div>
          <button
            type="button"
            className="tpo-btn tpo-btn--ghost tpo-btn--sm tpo-enroll-wizard-modal__close"
            disabled={submitting}
            onClick={onClose}
          >
            {t('moduleShell.close')}
          </button>
        </div>

        {sessionsError && step === STEP_SESSIONS ? (
          <p className="tpo-enroll__error tpo-enroll-wizard-modal__error">{sessionsError}</p>
        ) : null}

        <div className="tpo-enroll-wizard-modal__body">
          <div className="tpo-enroll-wizard-modal__table-wrap">
            <div
              className="tpo-enroll-wizard-modal__scroll"
              role="region"
              aria-label={step === STEP_SESSIONS ? '活动场次列表' : '经营站点列表'}
            >
              {step === STEP_SESSIONS ? (
                <table className="tpo-publish__table tpo-drafts__table tpo-ops-table tpo-ops-table--sticky-head tpo-enroll-wizard-modal__table">
                  <colgroup>
                    <col className="tpo-enroll-wizard-modal__col--check" />
                    <col />
                    <col />
                    <col />
                    <col />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="tpo-ops-th--check tpo-enroll-wizard-modal__th--check"
                        onClick={() => {
                          if (!sessionsLoading && pageSessionIds.length > 0) toggleAllSessions();
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={allPageSessionsSelected}
                          disabled={sessionsLoading || pageSessionIds.length === 0}
                          aria-label={fixedUi("全选当前页场次")}
                          onClick={(e) => e.stopPropagation()}
                          onChange={toggleAllSessions}
                        />
                      </th>
                      <th scope="col">{fixedUi("场次")}</th>
                      <th scope="col">{fixedUi("状态")}</th>
                      <th scope="col">{fixedUi("站点")}</th>
                      <th scope="col">{fixedUi("开始")}</th>
                      <th scope="col">{fixedUi("结束")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionsLoading ? (
                      <tr>
                        <td colSpan={6} className="tpo-enroll__empty-cell">
                          <div className="tpo-enroll__empty">
                            <p className="tpo-enroll__empty-title">{fixedUi("加载场次中…")}</p>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                    {!sessionsLoading && sessionList.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="tpo-enroll__empty-cell">
                          <div className="tpo-enroll__empty">
                            <p className="tpo-enroll__empty-title">{fixedUi("暂无场次")}</p>
                            <p className="tpo-enroll__empty-hint">{fixedUi("已按当前活动与所选货品查询场次列表。")}</p>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                    {!sessionsLoading && pageRows.map((row) => {
                      const sid = row.sessionId;
                      const checked = sid != null && selectedSessionIds.has(String(sid));
                      return (
                        <tr
                          key={String(sid || `${row.sessionName}-${row.startTime}`)}
                          className={checked ? 'is-selected' : undefined}
                          onClick={() => toggleSession(sid)}
                        >
                          <td className="tpo-ops-td--check tpo-enroll-wizard-modal__td--check">
                            <input
                              type="checkbox"
                              disabled={sid == null}
                              checked={checked}
                              aria-label={`选择场次 ${sid}`}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleSession(sid)}
                            />
                          </td>
                          <td>{row.sessionName || sid || '—'}</td>
                          <td>{row.sessionStatusLabel || '—'}</td>
                          <td>{row.siteName || row.siteId || '—'}</td>
                          <td>{row.startTime || row.startDateStr || '—'}</td>
                          <td>{row.endTime || row.endDateStr || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <table className="tpo-publish__table tpo-drafts__table tpo-ops-table tpo-ops-table--sticky-head tpo-enroll-wizard-modal__table">
                  <colgroup>
                    <col className="tpo-enroll-wizard-modal__col--check" />
                    <col />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="tpo-ops-th--check tpo-enroll-wizard-modal__th--check"
                        onClick={() => {
                          if (pageSiteIds.length > 0) toggleAllSites();
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={allPageSitesSelected}
                          disabled={pageSiteIds.length === 0}
                          aria-label={fixedUi("全选当前页站点")}
                          onClick={(e) => e.stopPropagation()}
                          onChange={toggleAllSites}
                        />
                      </th>
                      <th scope="col">{fixedUi("站点 ID")}</th>
                      <th scope="col">{fixedUi("站点名称")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {siteOptions.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="tpo-enroll__empty-cell">
                          <div className="tpo-enroll__empty">
                            <p className="tpo-enroll__empty-title">{fixedUi("暂无可选站点")}</p>
                            <p className="tpo-enroll__empty-hint">
                              {needsSessions ? '请返回上一步重新选择场次。' : '当前商品未返回站点信息。'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                    {pageRows.map((row) => {
                      const checked = selectedSiteIds.has(String(row.siteId));
                      return (
                        <tr
                          key={String(row.siteId)}
                          className={checked ? 'is-selected' : undefined}
                          onClick={() => toggleSite(row.siteId)}
                        >
                          <td className="tpo-ops-td--check tpo-enroll-wizard-modal__td--check">
                            <input
                              type="checkbox"
                              checked={checked}
                              aria-label={`选择站点 ${row.siteId}`}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleSite(row.siteId)}
                            />
                          </td>
                          <td>{row.siteId}</td>
                          <td>{row.siteName || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="tpo-enroll-wizard-modal__pager">
              <TemuOpsPager
                page={safePage}
                pageSize={pageSize}
                total={total}
                loading={sessionsLoading && step === STEP_SESSIONS}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                ariaLabel={step === STEP_SESSIONS ? '场次分页' : '站点分页'}
              />
            </div>
          </div>
        </div>

        <div className="tpo-batch-modal__foot tpo-enroll-wizard-modal__foot">
          <div className="tpo-enroll-wizard-modal__foot-summary">
            {step === STEP_SESSIONS
              ? `已选场次 ${selectedSessionIds.size}`
              : `已选站点 ${selectedSiteIds.size}`}
          </div>
          <div className="tpo-enroll-wizard-modal__foot-actions">
            {step === STEP_SITES && needsSessions ? (
              <button
                type="button"
                className="tpo-btn tpo-btn--secondary"
                disabled={submitting}
                onClick={() => setStep(STEP_SESSIONS)}
              >
                {t('marketing.enBackStep')}
              </button>
            ) : (
              <button
                type="button"
                className="tpo-btn tpo-btn--secondary"
                disabled={submitting}
                onClick={onClose}
              >
                {t('shop.cancel')}
              </button>
            )}
            {step === STEP_SESSIONS ? (
              <button
                type="button"
                className="tpo-btn tpo-btn--primary"
                disabled={selectedSessionIds.size === 0 || sessionsLoading}
                onClick={goNextFromSessions}
              >
                {t('marketing.enNext')}
              </button>
            ) : (
              <button
                type="button"
                className="tpo-btn tpo-btn--primary"
                disabled={submitting || selectedSiteIds.size === 0}
                onClick={handleSubmit}
              >
                {submitting ? t('returnList.submitting') : t('marketing.enSubmitCount', { count: productCount })}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
